// ShapeFilter.tsx - Updated with owner filter
import {
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import React, { SetStateAction, useMemo } from 'react';
import { capitalize } from '@ais-ui/utils';
import { ShapeFilters, ShapeFilterType } from './types';
import { useLocalization } from '@ais-ui/locale-management';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { FilterTrigger } from '../../components/FilterTrigger';
import { useProfile } from '@ais-ui/profile';
import { GeoJson } from '@ais-ui/api-spec/shapes';

interface ShapeFiltersProps {
  shapeFilters: ShapeFilters;
  setShapeFilters: React.Dispatch<SetStateAction<ShapeFilters>>;
  shapes?: GeoJson[];
}

// Extract unique owners from shapes (including nested shapes)
const getOwnerOptions = (
  shapes: GeoJson[] | undefined,
  currentUsername: string | undefined
): Array<{ value: string; label: string }> => {
  if (!shapes || shapes.length === 0) {
    return [];
  }

  const ownerSet = new Set<string>();

  // Recursive function to collect all owners
  const collectOwners = (shape: GeoJson) => {
    if (shape.properties?.owner) {
      ownerSet.add(shape.properties.owner);
    }
    // If it's a FeatureCollection, check nested features
    if (shape.type === 'FeatureCollection' && shape.features) {
      shape.features.forEach(collectOwners);
    }
  };

  shapes.forEach(collectOwners);

  const uniqueOwners = Array.from(ownerSet);

  // Separate current user from other owners
  const isCurrentUser = (owner: string) => owner === currentUsername;
  const currentUserOwner = uniqueOwners.find(isCurrentUser);
  const otherOwners = uniqueOwners
    .filter((owner) => !isCurrentUser(owner))
    .sort((a, b) => a.localeCompare(b));

  // Build the final list with current user first (as "Me")
  const result: Array<{ value: string; label: string }> = [];

  if (currentUserOwner) {
    result.push({ value: currentUserOwner, label: 'Me' });
  }

  otherOwners.forEach((owner) => {
    result.push({ value: owner, label: owner });
  });

  return result;
};

export const ShapeFilter = ({
  shapeFilters,
  setShapeFilters,
  shapes,
}: ShapeFiltersProps) => {
  const { toast } = useToast();
  const { messages } = useLocalization();
  const permissions = usePermissions();
  const { username } = useProfile();

  const typeOptions: ShapeFilterType[] = [
    'all',
    'polygon',
    'circle',
    'linestring',
    'point',
  ];

  const ownerOptions = useMemo(
    () => getOwnerOptions(shapes, username),
    [shapes, username]
  );

  const numberOfActiveFilters = useMemo(() => {
    let counter = 0;
    if (shapeFilters.type !== 'all') {
      counter = counter + 1;
    }
    if (shapeFilters.owner !== 'all') {
      counter = counter + 1;
    }
    if (shapeFilters.hasRoutes) {
      counter = counter + 1;
    }
    if (shapeFilters.favorites) {
      counter = counter + 1;
    }
    return counter;
  }, [shapeFilters]);

  const handleFilterChange = <K extends keyof ShapeFilters>(
    key: K,
    value: ShapeFilters[K]
  ) => {
    setShapeFilters((prev: ShapeFilters) => ({
      ...prev,
      [key]: value,
    }));

    if (key === 'type') {
      const description = `Shapes filtered by ${value} type`;
      toast({
        title: messages.genericToastTitleSuccess,
        description: description,
      });
    } else if (key === 'owner') {
      const description =
        value === 'all'
          ? 'Showing all shapes'
          : `Shapes filtered by owner: ${value}`;
      toast({
        title: messages.genericToastTitleSuccess,
        description: description,
      });
    } else if (key === 'hasRoutes') {
      const description = value
        ? 'Shapes filtered by routes'
        : 'Shapes not filtered by routes';
      toast({
        title: messages.genericToastTitleSuccess,
        description: description,
      });
    } else if (key === 'favorites') {
      const description = value
        ? 'Shapes filtered by favorites'
        : 'Shapes not filtered by favorites';
      toast({
        title: messages.genericToastTitleSuccess,
        description: description,
      });
    } else {
      toast({
        title: messages.genericToastTitleSuccess,
        description: 'Updated shapes filter',
      });
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <FilterTrigger activeFilterCount={numberOfActiveFilters} />
      </PopoverTrigger>
      <PopoverContent side="left" align="start">
        <div className="mb-4 space-y-2">
          <Label className="text-lg">Filter shapes</Label>
          <Separator />
        </div>
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select
              value={shapeFilters.type}
              onValueChange={(value: ShapeFilterType) =>
                handleFilterChange('type', value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {capitalize(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {ownerOptions.length > 0 && (
            <div className="space-y-2">
              <Label>Owner</Label>
              <Select
                value={shapeFilters.owner}
                onValueChange={(value) => handleFilterChange('owner', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Owners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Owners</SelectItem>
                  {ownerOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {permissions.pilot && (
            <div className="flex items-center justify-between">
              <Label>Has Routes</Label>
              <Switch
                checked={shapeFilters.hasRoutes}
                onCheckedChange={(checked) =>
                  handleFilterChange('hasRoutes', checked)
                }
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label>Favorites</Label>
            <Switch
              checked={shapeFilters.favorites}
              onCheckedChange={(checked) =>
                handleFilterChange('favorites', checked)
              }
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
